import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../api/admin.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { useSocket } from '../hooks/useSocket.jsx';
import { nowOffice, formatDate } from '../lib/time.js';

export default function AdminDashboardPage() {
    const { user } = useAuth();
    const { connected: socketConnected } = useSocket();

    const { data, isLoading, isError } = useQuery({
        queryKey: ['admin', 'summary'],
        queryFn: () => adminApi.summary(),
        refetchInterval: 60 * 1000,
    });

    return (
        <>
            {/* ----- Header (matches employee dashboard's header style) ----- */}
            <header className="flex items-center justify-between pb-3.5 mb-5"
                style={{ borderBottom: '0.5px solid var(--color-border-tertiary)' }}>
                <div>
                    <p className="eyebrow m-0">{formatDate(nowOffice())}</p>
                    <p className="text-lg font-medium" style={{ marginTop: 2 }}>
                        Today at a glance{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
                    </p>
                </div>
                <div className="flex items-center" style={{ gap: 12 }}>
                    <span className="flex items-center" style={{ gap: 8, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                        <span style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: socketConnected ? 'var(--color-status-complete)' : 'var(--color-status-weekend)',
                        }} />
                        {socketConnected ? 'Live' : 'Offline'}
                    </span>
                    <Link to="/admin/employees/new" className="btn btn-primary">
                        + Add employee
                    </Link>
                </div>
            </header>

            {isLoading && <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading…</p>}
            {isError && <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>Could not load summary.</p>}

            {data && (
                <>
                    {/* ----- 5 stat tiles ----- */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                        <Tile
                            label="Total active"
                            value={data.total_active_employees}
                        />
                        <Tile
                            label="Currently in"
                            value={data.currently_checked_in}
                            tone={data.currently_checked_in > 0 ? 'positive' : null}
                        />
                        <Tile
                            label="Not in yet"
                            value={data.not_checked_in_yet_today}
                            tone={data.not_checked_in_yet_today > 0 ? 'warn' : null}
                        />
                        <Tile
                            label="Late today"
                            value={data.late_today}
                            tone={data.late_today > 0 ? 'warn' : null}
                        />
                        <Tile
                            label="Cron failures (24h)"
                            value={data.cron_failures_24h}
                            tone={data.cron_failures_24h > 0 ? 'danger' : null}
                        />
                    </div>

                    {/* ----- Quick actions card ----- */}
                    <div className="surface-card" style={{ padding: '1rem 1.25rem' }}>
                        <p style={{ fontSize: 14, fontWeight: 500, margin: 0, marginBottom: 12 }}>
                            Quick actions
                        </p>
                        <div className="flex flex-wrap" style={{ gap: 8 }}>
                            <Link to="/admin/presence" className="btn btn-soft">
                                Live presence →
                            </Link>
                            <Link to="/admin/employees" className="btn btn-soft">
                                All employees
                            </Link>
                            <Link to="/admin/leave" className="btn btn-soft">
                                Leave queue
                            </Link>
                            <Link to="/admin/employees?active=false" className="btn btn-ghost">
                                View deactivated
                            </Link>
                            <Link to="/admin/employees?role=manager" className="btn btn-ghost">
                                Managers only
                            </Link>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

function Tile({ label, value, tone }) {
    const toneConfig = {
        positive: {
            bg: 'rgba(29, 158, 117, 0.08)',
            border: 'rgba(29, 158, 117, 0.35)',
            valueColor: 'var(--color-status-complete)',
        },
        warn: {
            bg: 'rgba(239, 159, 39, 0.10)',
            border: 'rgba(239, 159, 39, 0.40)',
            valueColor: '#A56600',
        },
        danger: {
            bg: 'var(--color-pill-rejected-bg)',
            border: 'var(--color-danger-border)',
            valueColor: 'var(--color-danger)',
        },
    };
    const config = tone ? toneConfig[tone] : null;
    const style = config
        ? {
            background: config.bg,
            border: `0.5px solid ${config.border}`,
        }
        : null;

    return (
        <div className="surface-card" style={{ padding: '14px 16px', ...(style || {}) }}>
            <p className="eyebrow m-0">{label}</p>
            <p className="tnum"
                style={{
                    fontSize: 28,
                    fontWeight: 500,
                    marginTop: 6,
                    color: config?.valueColor || 'var(--color-text-primary)',
                }}
            >
                {value}
            </p>
        </div>
    );
}
